import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type Context,
  type DragEvent,
  type ReactNode
} from 'react';
import {
  ArrowLeftIcon,
  BookIcon,
  BuildingIcon,
  ChalkboardTeacherIcon,
  ChartLineIcon,
  ChatIcon,
  ClipboardTextIcon,
  CubeIcon,
  DotsThreeIcon,
  FolderIcon,
  GearIcon,
  GraduationCapIcon,
  HandshakeIcon,
  HouseIcon,
  ListChecksIcon,
  PencilSimpleLineIcon,
  PresentationChartIcon,
  UserIcon,
  UsersThreeIcon,
  type Icon
} from '@phosphor-icons/react';
import { Link, useLocation } from 'react-router-dom';
import useTenantNavigate from 'src/hooks/useTenantNavigate';
import { Menu, MenuItem } from 'src/components/ui/Menu';
import { isReservedAppPrefix, resolveTenantCode, toTenantPath } from 'src/lib/tenantRouting';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  breadcrumbPageClassName
} from 'src/components/ui/breadcrumb';
import Button from 'src/components/ui/Button';
import { setDragHint } from 'src/lib/dragHint';

export type PageHeaderCrumb = {
  label: string;
  /** Tenant-relative path. Omit on the current page. */
  to?: string;
  /** Called when a dragged item is dropped on this crumb. */
  onDrop?: (event: DragEvent) => void;
};

const SEGMENT_LABELS: Record<string, string> = {
  home: 'Home',
  chat: 'Chat',
  write: 'Write',
  review: 'Review',
  groups: 'Groups',
  actions: 'Actions',
  modules: 'Modules',
  documents: 'Document Hub',
  alerts: 'Alerts',
  wiki: 'Regulatory Wiki',
  reporting: 'Dashboards',
  tutorials: 'Help & Tutorials',
  profile: 'Profile',
  settings: 'Settings',
  admin: 'Admin',
  dashboard: 'Usage dashboard',
  meetings: 'Meetings',
  finalise: 'Finalise',
  'data-library': 'Data library',
  pfi: 'PFI',
  obligations: 'Obligation register',
  pam: 'PAM',
  'training-hub': 'Training Hub',
  'asset-review': 'Asset review',
  'help-support': 'Help and support',
  activity: 'Activity',
  notifications: 'Notifications',
  'delete-account': 'Delete account',
  organisation: 'General',
  members: 'Members',
  guests: 'Guests',
  sites: 'Sites',
  domains: 'Domains',
  'ai-controls': 'AI controls'
};

/** Labels for settings pages whose segment means something else elsewhere, such as the Training Hub's Appointments tab. */
const SETTINGS_SEGMENT_LABELS: Record<string, string> = {
  appointments: 'Appointments'
};

/** Group hub tabs share the Groups icon. They are not their own features. */
const GROUP_HUB_TAB_SEGMENTS = new Set([
  'home',
  'actions',
  'meetings',
  'files',
  'evidence',
  'people',
  'training',
  'pam',
  'dashboards'
]);

const FEATURE_ICONS: Record<string, Icon> = {
  home: HouseIcon,
  chat: ChatIcon,
  write: PencilSimpleLineIcon,
  review: ClipboardTextIcon,
  groups: UsersThreeIcon,
  meetings: UsersThreeIcon,
  actions: ListChecksIcon,
  modules: CubeIcon,
  documents: FolderIcon,
  pfi: HandshakeIcon,
  pam: BuildingIcon,
  'training-hub': GraduationCapIcon,
  wiki: BookIcon,
  dashboard: ChartLineIcon,
  reporting: PresentationChartIcon,
  tutorials: ChalkboardTeacherIcon,
  profile: UserIcon,
  settings: GearIcon,
  admin: GearIcon
};

type BreadcrumbSlotStore<T> = {
  get: () => T;
  set: (next: T) => void;
  subscribe: (listener: () => void) => () => void;
};

type BreadcrumbItemsStore = {
  get: () => PageHeaderCrumb[] | null;
  set: (next: PageHeaderCrumb[] | null | ((current: PageHeaderCrumb[] | null) => PageHeaderCrumb[] | null)) => void;
  subscribe: (listener: () => void) => () => void;
};

export type PageHeaderOnBack = () => boolean | void | Promise<boolean | void>;

const PageHeaderItemsStoreContext = createContext<BreadcrumbItemsStore | null>(null);
const PageHeaderActionsStoreContext = createContext<BreadcrumbSlotStore<ReactNode> | null>(null);
const PageHeaderMetaStoreContext = createContext<BreadcrumbSlotStore<ReactNode> | null>(null);
const PageHeaderPageStoreContext = createContext<BreadcrumbSlotStore<ReactNode> | null>(null);
const PageHeaderOnBackStoreContext = createContext<BreadcrumbSlotStore<PageHeaderOnBack | null> | null>(null);

function createSlotStore<T>(initial: T): BreadcrumbSlotStore<T> {
  let value = initial;
  const listeners = new Set<() => void>();
  return {
    get: () => value,
    set: (next) => {
      if (Object.is(next, value)) return;
      value = next;
      listeners.forEach((listener) => listener());
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    }
  };
}

function createBreadcrumbItemsStore(): BreadcrumbItemsStore {
  let items: PageHeaderCrumb[] | null = null;
  const listeners = new Set<() => void>();
  return {
    get: () => items,
    set: (next) => {
      const resolved = typeof next === 'function' ? next(items) : next;
      if (resolved === items) return;
      items = resolved;
      listeners.forEach((listener) => listener());
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    }
  };
}

export function PageHeaderProvider({ children }: { children: ReactNode }) {
  const itemsStore = useRef<BreadcrumbItemsStore | null>(null);
  const actionsStore = useRef<BreadcrumbSlotStore<ReactNode> | null>(null);
  const metaStore = useRef<BreadcrumbSlotStore<ReactNode> | null>(null);
  const pageStore = useRef<BreadcrumbSlotStore<ReactNode> | null>(null);
  const onBackStore = useRef<BreadcrumbSlotStore<PageHeaderOnBack | null> | null>(null);
  if (!itemsStore.current) itemsStore.current = createBreadcrumbItemsStore();
  if (!actionsStore.current) actionsStore.current = createSlotStore<ReactNode>(null);
  if (!metaStore.current) metaStore.current = createSlotStore<ReactNode>(null);
  if (!pageStore.current) pageStore.current = createSlotStore<ReactNode>(null);
  if (!onBackStore.current) onBackStore.current = createSlotStore<PageHeaderOnBack | null>(null);
  return (
    <PageHeaderItemsStoreContext.Provider value={itemsStore.current}>
      <PageHeaderActionsStoreContext.Provider value={actionsStore.current}>
        <PageHeaderMetaStoreContext.Provider value={metaStore.current}>
          <PageHeaderPageStoreContext.Provider value={pageStore.current}>
            <PageHeaderOnBackStoreContext.Provider value={onBackStore.current}>
              {children}
            </PageHeaderOnBackStoreContext.Provider>
          </PageHeaderPageStoreContext.Provider>
        </PageHeaderMetaStoreContext.Provider>
      </PageHeaderActionsStoreContext.Provider>
    </PageHeaderItemsStoreContext.Provider>
  );
}

/**
 * Replace the route-derived trail while this component is mounted.
 * Pass `null` to keep the route trail.
 */
export function usePageHeader(items: PageHeaderCrumb[] | null) {
  const store = useContext(PageHeaderItemsStoreContext);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const key = items === null ? '\0' : items.map((item) => `${item.label}\0${item.to ?? ''}`).join('\n');

  useEffect(() => {
    if (!store) return;
    const snapshot = itemsRef.current;
    // null means "leave the current trail alone" (embedded views, still loading).
    if (snapshot === null) return;
    store.set(snapshot);
    // A route change can unmount the previous page after the next page has
    // already published its trail. Only clear when this snapshot is still current.
    return () => store.set((current) => (current === snapshot ? null : current));
  }, [store, key]);
}

/** Render details immediately after the trail while this page is mounted. */
export function usePageHeaderMeta(meta: ReactNode) {
  const store = useContext(PageHeaderMetaStoreContext);
  const metaRef = useRef(meta);
  metaRef.current = meta;

  useLayoutEffect(() => {
    store?.set(metaRef.current);
  });

  useLayoutEffect(() => {
    return () => store?.set(null);
  }, [store]);
}

/** Render controls on the right of the breadcrumb bar while this page is mounted. */
export function usePageHeaderActions(actions: ReactNode) {
  const store = useContext(PageHeaderActionsStoreContext);
  const actionsRef = useRef(actions);
  actionsRef.current = actions;

  useLayoutEffect(() => {
    store?.set(actionsRef.current);
  });

  useLayoutEffect(() => {
    return () => store?.set(null);
  }, [store]);
}

function useBreadcrumbSlot(context: Context<BreadcrumbSlotStore<ReactNode> | null>) {
  const store = useContext(context);
  const subscribe = useCallback((listener: () => void) => (store ? store.subscribe(listener) : () => {}), [store]);
  const get = useCallback(() => store?.get() ?? null, [store]);
  return useSyncExternalStore(subscribe, get, get);
}

function useBreadcrumbItems() {
  const store = useContext(PageHeaderItemsStoreContext);
  const subscribe = useCallback((listener: () => void) => (store ? store.subscribe(listener) : () => {}), [store]);
  const get = useCallback(() => store?.get() ?? null, [store]);
  return useSyncExternalStore(subscribe, get, get);
}

function useBreadcrumbActions() {
  return useBreadcrumbSlot(PageHeaderActionsStoreContext);
}

function useBreadcrumbMeta() {
  return useBreadcrumbSlot(PageHeaderMetaStoreContext);
}

/** Replace the current crumb's label while this page is mounted. */
export function usePageHeaderPage(content: ReactNode) {
  const store = useContext(PageHeaderPageStoreContext);
  const contentRef = useRef(content);
  contentRef.current = content;

  useLayoutEffect(() => {
    store?.set(contentRef.current);
  });

  useLayoutEffect(() => {
    return () => store?.set(null);
  }, [store]);
}

function useBreadcrumbPage() {
  return useBreadcrumbSlot(PageHeaderPageStoreContext);
}

/** Run before the shared header back arrow navigates. Return false to stay. */
export function usePageHeaderOnBack(onBack: PageHeaderOnBack | null) {
  const store = useContext(PageHeaderOnBackStoreContext);
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;

  useLayoutEffect(() => {
    store?.set(onBackRef.current);
  });

  useLayoutEffect(() => {
    return () => store?.set(null);
  }, [store]);
}

function useBreadcrumbOnBack() {
  const store = useContext(PageHeaderOnBackStoreContext);
  const subscribe = useCallback((listener: () => void) => (store ? store.subscribe(listener) : () => {}), [store]);
  const get = useCallback(() => store?.get() ?? null, [store]);
  return useSyncExternalStore(subscribe, get, get);
}

function appSegments(pathname: string): string[] {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) return [];
  if (!isReservedAppPrefix(parts[0])) return parts.slice(1);
  return parts;
}

export function breadcrumbsFromPath(pathname: string): PageHeaderCrumb[] {
  const crumbs: PageHeaderCrumb[] = [];
  let path = '';

  const segments = appSegments(pathname);
  if (segments.length === 1 && segments[0] === 'home') return [];
  // These features are top-level in the trail. Their URLs still live under /modules.
  const hideModules = segments[0] === 'modules' && ['pam', 'pfi', 'training-hub'].includes(segments[1]);
  for (const segment of segments) {
    path += `/${segment}`;
    if (hideModules && segment === 'modules') continue;
    // Group hub tabs (/groups/:id/home, /actions, …) are not trail steps.
    // The group page supplies the group name.
    if (segments[0] === 'groups' && GROUP_HUB_TAB_SEGMENTS.has(segment)) continue;
    const label =
      (segments[0] === 'settings' ? SETTINGS_SEGMENT_LABELS[segment] : undefined) ?? SEGMENT_LABELS[segment];
    if (!label) continue;
    crumbs.push({ label, to: segment === 'modules' ? undefined : path });
  }

  if (segments[0] === 'settings' && segments.length === 1) {
    crumbs.push({ label: 'Account' });
  }

  if (crumbs.length > 0) {
    crumbs[crumbs.length - 1] = { label: crumbs[crumbs.length - 1].label };
  }

  return crumbs;
}

function featureIcon(pathname: string): Icon | undefined {
  const segments = appSegments(pathname);
  const inGroupHub = segments[0] === 'groups';
  const segment = segments.find((part, index) => {
    if (part === 'modules' && ['pam', 'pfi', 'training-hub'].includes(segments[index + 1])) return false;
    if (inGroupHub && GROUP_HUB_TAB_SEGMENTS.has(part)) return false;
    return FEATURE_ICONS[part] !== undefined;
  });
  return segment ? FEATURE_ICONS[segment] : undefined;
}

type HeaderFit = 'full' | 'no-meta' | 'icons' | 'crumbs';

/** Extra space required before a collapsed piece comes back, so the bar doesn't flicker. */
const HEADER_FIT_HYSTERESIS = 16;

// Survives AppLayout remounts (chat uses a different layout instance than the rest of the app).
let trackedPath: string | null = null;
let trackedPrevious: string | null = null;

export function PageHeader({ variant = 'desktop' }: { variant?: 'desktop' | 'mobile' }) {
  const override = useBreadcrumbItems();
  const actions = useBreadcrumbActions();
  const meta = useBreadcrumbMeta();
  const pageContent = useBreadcrumbPage();
  const onBack = useBreadcrumbOnBack();
  const location = useLocation();
  const navigate = useTenantNavigate();
  const pathname = location.pathname;
  const crumbs = override ?? breadcrumbsFromPath(pathname);
  const tenantCode = resolveTenantCode(pathname);
  const FeatureIcon = featureIcon(pathname);
  const currentPath = `${location.pathname}${location.search}`;
  const [previousPath, setPreviousPath] = useState<string | null>(() =>
    trackedPath && trackedPath !== currentPath ? trackedPath : trackedPrevious
  );
  const [fit, setFit] = useState<HeaderFit>('full');
  const [backBusy, setBackBusy] = useState(false);
  const [crumbMenuAnchor, setCrumbMenuAnchor] = useState<HTMLElement | null>(null);
  const backInFlight = useRef(false);
  const rowRef = useRef<HTMLDivElement>(null);
  const crumbRef = useRef<HTMLDivElement>(null);
  const metaSlotRef = useRef<HTMLDivElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);
  const widths = useRef({ meta: 0, actionsLabeled: 0, actionsIcon: 0, crumbs: 0 });

  useEffect(() => {
    if (trackedPath !== currentPath) {
      trackedPrevious = trackedPath;
      trackedPath = currentPath;
      setPreviousPath(trackedPrevious);
    }
  }, [currentPath]);

  const crumbKey = crumbs.map((crumb) => crumb.label).join('\n');
  useLayoutEffect(() => {
    if (variant === 'mobile') return;
    const row = rowRef.current;
    if (!row) return;

    const measure = () => {
      const crumbEl = crumbRef.current;
      const metaEl = metaSlotRef.current;
      const actionsEl = actionsRef.current;
      if (fit !== 'crumbs' && crumbEl) widths.current.crumbs = crumbEl.offsetWidth;
      if (fit === 'full' && metaEl) widths.current.meta = metaEl.offsetWidth;
      if ((fit === 'full' || fit === 'no-meta') && actionsEl) widths.current.actionsLabeled = actionsEl.offsetWidth;
      if ((fit === 'icons' || fit === 'crumbs') && actionsEl) widths.current.actionsIcon = actionsEl.offsetWidth;

      // ml-auto eats spare width inside the flex row, so clientWidth - scrollWidth stays ~0
      // once nothing overflows. Sum the items themselves to see the real gap.
      const gap = Number.parseFloat(getComputedStyle(row).columnGap) || 0;
      const children = [...row.children] as HTMLElement[];
      const used = children.reduce((sum, el) => sum + el.offsetWidth, 0) + gap * Math.max(0, children.length - 1);
      const free = row.clientWidth - used;
      // While the trail is shrinking it is flex-1, so spare width sits inside the crumb, not between items.
      const crumbSurplus = fit === 'crumbs' && crumbEl ? Math.max(0, crumbEl.offsetWidth - widths.current.crumbs) : 0;
      const room = free + crumbSurplus;
      const overflow = row.scrollWidth > row.clientWidth + 1;

      setFit((current) => {
        if (overflow) {
          if (current === 'full' && meta) return 'no-meta';
          if (current === 'full' || current === 'no-meta') return actions ? 'icons' : 'crumbs';
          if (current === 'icons') return 'crumbs';
          return current;
        }
        if (current === 'crumbs' && room >= HEADER_FIT_HYSTERESIS) {
          return actions ? 'icons' : meta ? 'no-meta' : 'full';
        }
        if (current === 'icons') {
          const need = widths.current.actionsLabeled - widths.current.actionsIcon;
          if (widths.current.actionsLabeled > 0 && room >= need + HEADER_FIT_HYSTERESIS) {
            return meta ? 'no-meta' : 'full';
          }
        }
        if (
          current === 'no-meta' &&
          widths.current.meta > 0 &&
          room >= widths.current.meta + gap + HEADER_FIT_HYSTERESIS
        ) {
          return 'full';
        }
        return current;
      });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    return () => observer.disconnect();
  }, [actions, crumbKey, fit, meta, variant]);

  if (crumbs.length === 0 && !actions) return null;

  const parentCrumb = crumbs.length > 1 ? crumbs[crumbs.length - 2] : undefined;
  const historyIndex = (window.history.state as { idx?: number } | null)?.idx ?? 0;
  const previousLabel = previousPath
    ? breadcrumbsFromPath(previousPath.split('?')[0]).at(-1)?.label
    : parentCrumb?.label;

  const handleBack = async () => {
    if (backInFlight.current) return;
    backInFlight.current = true;
    setBackBusy(true);
    const pathAtStart = pathname;
    try {
      if (onBack) {
        const allowed = await onBack();
        if (allowed === false) return;
      }
      if (window.location.pathname !== pathAtStart) return;
      if (historyIndex > 0) {
        navigate(-1);
        return;
      }
      if (parentCrumb?.to) {
        navigate(parentCrumb.to);
        return;
      }
      navigate('/home');
    } finally {
      backInFlight.current = false;
      setBackBusy(false);
    }
  };

  const isMobile = variant === 'mobile';
  const inverseMobile = isMobile;
  const shrink = isMobile || fit === 'crumbs';
  const showMeta = !isMobile && Boolean(meta) && fit === 'full';
  const iconOnly = isMobile || fit === 'icons' || fit === 'crumbs';
  const tailCount = shrink ? 1 : 3;
  const collapsedCrumbs = crumbs.length > tailCount + 1;
  const parentIndex = crumbs.length - 2;
  const hiddenCrumbs = collapsedCrumbs
    ? crumbs.slice(1, crumbs.length - tailCount).filter((_, index) => {
        const crumbIndex = index + 1;
        return !(crumbs[crumbIndex]?.onDrop && crumbIndex === parentIndex);
      })
    : [];

  return (
    <div
      className={
        isMobile
          ? 'flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden'
          : 'sticky top-16 z-30 hidden h-14 shrink-0 items-center gap-2 px-4 py-1 md:top-0 md:flex md:px-8'
      }
    >
      {isMobile ? (
        <Button
          type="button"
          variant="outline"
          size="md"
          icon={ArrowLeftIcon}
          aria-label={previousLabel ? `Back to ${previousLabel}` : 'Back'}
          disabled={backBusy}
          onClick={handleBack}
        />
      ) : (
        <button
          type="button"
          className="inline-flex size-7 shrink-0 items-center justify-center rounded-md text-content-subtle hover:bg-surface-hover hover:text-content-default"
          aria-label={previousLabel ? `Back to ${previousLabel}` : 'Back'}
          disabled={backBusy}
          onClick={handleBack}
        >
          <ArrowLeftIcon size={16} />
        </button>
      )}
      <div ref={rowRef} className="flex min-w-0 flex-1 items-center gap-4 overflow-hidden">
        <div ref={crumbRef} className={shrink ? 'min-w-0 flex-1 overflow-hidden' : 'shrink-0'}>
          <Breadcrumb>
            <BreadcrumbList
              className={
                shrink
                  ? `w-full min-w-0 flex-nowrap text-sm md:text-base ${inverseMobile ? 'text-white/70' : ''}`
                  : 'w-max flex-nowrap text-base'
              }
            >
              {crumbs.map((crumb, index) => {
                const parentIndex = crumbs.length - 2;
                const keepParent = !!crumb.onDrop && index === parentIndex;
                if (collapsedCrumbs && index > 0 && index < crumbs.length - tailCount && !keepParent) {
                  if (index > 1) return null;
                  return [
                    <BreadcrumbSeparator key="sep-ellipsis" />,
                    <BreadcrumbItem key="ellipsis">
                      <button
                        type="button"
                        className="inline-flex size-5 items-center justify-center rounded-md text-content-subtle hover:bg-surface-hover hover:text-content-default"
                        aria-label="Show hidden folders"
                        aria-expanded={crumbMenuAnchor != null}
                        onClick={(event) => setCrumbMenuAnchor(event.currentTarget)}
                      >
                        <DotsThreeIcon size={16} weight="bold" />
                      </button>
                    </BreadcrumbItem>
                  ];
                }
                const isLast = index === crumbs.length - 1;
                const icon =
                  index === 0 && FeatureIcon ? (
                    <FeatureIcon size={20} weight="regular" className="block shrink-0" aria-hidden />
                  ) : null;
                const label =
                  shrink && isLast && !pageContent ? (
                    <span className="min-w-0 truncate">{crumb.label}</span>
                  ) : (
                    crumb.label
                  );
                const canDrop = !!crumb.onDrop && !isLast;
                const item = (
                  <BreadcrumbItem
                    key={`${crumb.label}-${crumb.to ?? 'current'}`}
                    className={shrink && isLast ? 'min-w-0 shrink' : 'shrink-0'}
                    onDragOver={
                      canDrop
                        ? (event) => {
                            if (event.dataTransfer.types.includes('Files')) return;
                            const hubDrag =
                              event.dataTransfer.types.includes('application/x-hub-folder') ||
                              event.dataTransfer.types.includes('application/x-hub-document');
                            if (!hubDrag) return;
                            event.preventDefault();
                            event.dataTransfer.dropEffect = 'move';
                            setDragHint(`Move to ${crumb.label}`);
                          }
                        : undefined
                    }
                    onDragLeave={
                      canDrop
                        ? (event) => {
                            if (event.currentTarget.contains(event.relatedTarget as Node)) return;
                            setDragHint(null);
                          }
                        : undefined
                    }
                    onDrop={
                      canDrop
                        ? (event) => {
                            event.preventDefault();
                            setDragHint(null);
                            crumb.onDrop?.(event);
                          }
                        : undefined
                    }
                  >
                    {isLast && pageContent ? (
                      <div
                        aria-current="page"
                        className={`${breadcrumbPageClassName} flex min-w-0 items-center overflow-hidden ${inverseMobile ? 'text-white' : ''}`}
                      >
                        {pageContent}
                      </div>
                    ) : isLast ? (
                      <BreadcrumbPage
                        className={`inline-flex min-w-0 items-center gap-2 ${inverseMobile ? 'text-white' : ''}`}
                      >
                        {icon}
                        {label}
                      </BreadcrumbPage>
                    ) : crumb.to ? (
                      <BreadcrumbLink
                        className="inline-flex items-center gap-2"
                        render={<Link to={toTenantPath(crumb.to, tenantCode)} />}
                      >
                        {icon}
                        {crumb.label}
                      </BreadcrumbLink>
                    ) : (
                      <span className="inline-flex items-center gap-1.5">{crumb.label}</span>
                    )}
                  </BreadcrumbItem>
                );
                if (index === 0) return item;
                return [<BreadcrumbSeparator key={`sep-${crumb.label}-${index}`} />, item];
              })}
            </BreadcrumbList>
          </Breadcrumb>
          <Menu
            anchorEl={crumbMenuAnchor}
            open={crumbMenuAnchor != null}
            onClose={() => setCrumbMenuAnchor(null)}
            placement="bottom-start"
          >
            {hiddenCrumbs.map((crumb) => (
              <MenuItem
                key={`${crumb.label}-${crumb.to ?? 'here'}`}
                onClick={() => {
                  setCrumbMenuAnchor(null);
                  if (crumb.to) navigate(crumb.to);
                }}
              >
                {crumb.label}
              </MenuItem>
            ))}
          </Menu>
        </div>
        {showMeta ? (
          <div ref={metaSlotRef} className="flex shrink-0 items-center">
            {meta}
          </div>
        ) : null}
        {actions ? (
          <div
            ref={actionsRef}
            className={`ml-auto flex shrink-0 items-center gap-2 ${
              iconOnly ? '[&_:is(button,a):has(svg)_[data-slot=button-label]]:sr-only' : ''
            }`}
          >
            {actions}
          </div>
        ) : null}
      </div>
    </div>
  );
}
